import React, { useState, useRef, useEffect } from "react";
import axios from "@/features/axiosHelper";
import { Box, Container, Typography } from "@mui/material";
import MainNavigation from "@/components/MainNavigation/MainNavigation";
import {
  MenuItemEnum,
  NotificationTypeEnum,
  SeverityEnum,
  NotificationTypeLabels,
  SeverityLabels,
} from "@/utils/utils";
import { ShepHorizontalMenu } from "@/components/GenericComponents/ShepComponents";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AddConfigurationPopup from "./AddConfigurationPopup";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import {
  ShepCheckbox,
  ShepPagination,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";
dayjs.extend(utc);

const NotificationConfig = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [addConfigurationOpen, setAddConfigurationOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState({});
  const [operations, setOperations] = useState([]);

  const getConfigurationText = (count) =>
    count === 1 ? "configuration" : "configurations";

  const gridRef = useRef(null);
  const columns = [
    {
      field: "Name",
      headerName: "Configuration Name",
      width: 350,
    },
    {
      field: "NotificationType",
      headerName: "Type",
      width: 600,
      renderCell: (params) => {
        return (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {params.value !== NotificationTypeEnum.Workflow ? (
              <div style={{ padding: "2px 0" }}>
                {NotificationTypeLabels[params.value]}
              </div>
            ) : (
              params.row.OperationIds.map((id) => {
                let typeLabel = "Workflow";
                const opName = operations.find(
                  (op) => op.OperationId === id,
                )?.OperationName;
                if (opName) typeLabel += ` - ${opName}`;
                return <div style={{ padding: "2px 0" }}>{typeLabel}</div>;
              })
            )}
          </Box>
        );
      },
    },
    {
      field: "Severities",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {params.value.map((severity) => {
              return (
                <div style={{ padding: "2px 0" }}>
                  {SeverityLabels[severity]}
                </div>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "threedotmenu",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          showInMenu
          onClick={() => {
            setSelectedConfiguration(params.row);
            setAddConfigurationOpen(true);
          }}
        />,
        <GridActionsCellItem
          label={`${params.row.IsPaused ? "Unpause" : "Pause"}`}
          showInMenu
          onClick={() => {
            if (params.row.IsPaused) {
              validateUnpause(params.row.NotificationConfigurationId);
            } else {
              validatePause(params.row.NotificationConfigurationId);
            }
          }}
        />,
        <GridActionsCellItem
          label="Delete"
          showInMenu
          onClick={() => validateDelete(params.row.NotificationConfigurationId)}
        />,
      ],
    },
  ];

  useEffect(() => {
    getNotificationConfigurations();
    getOperations();
  }, []);

  const getNotificationConfigurations = () => {
    axios
      .get("/notifications/configurations")
      .then((response) => {
        const responseData = response.data;
        setConfigurationGridLoaded(true);
        setRows(responseData ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const getOperations = () => {
    axios
      .get("/connections/operations")
      .then((response) => {
        const responseData = response.data;
        setOperations(responseData ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  //Notification functions-------//
  const actions = [
    { label: "Pause", onClick: () => validatePause() },
    { label: "Unpause", onClick: () => validateUnpause() },
    {
      label: "Add Configuration",
      onClick: () => {
        setSelectedConfiguration({});
        setAddConfigurationOpen(true);
      },
    },
    { label: "Delete", onClick: () => validateDelete(), variant: "danger" },
  ];

  const onAddConfigurationClosed = () => {
    setSelectedConfiguration({});
    setAddConfigurationOpen(false);
    getNotificationConfigurations();
  };

  const [configurationGridLoaded, setConfigurationGridLoaded] = useState(false);

  const validatePause = (configurationId = null) => {
    let configurationIds = [];
    if (configurationId !== null) {
      //single row, from 3 dot menu
      configurationIds = [configurationId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Configurations Selected",
            message: "Please select a configuration to pause.",
          }),
        );
        return;
      }
      configurationIds = selectedRows
        .filter((y) => y.IsPaused === false)
        .map((x) => x.NotificationConfigurationId);
    }
    if (configurationIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          title: "No Configurations to Pause",
          message: "Please select an unpaused configuration to pause.",
        }),
      );
      return;
    }
    const configurationText = getConfigurationText(configurationIds.length);
    const configurationIsPlural = configurationIds.length !== 1;
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: `${configurationIsPlural ? "These" : "This"} ${configurationText} ${configurationIsPlural ? "are" : "is"} currently unpaused`,
        message: `Are you sure you want to pause ${configurationIsPlural ? "these" : "this"} ${configurationText}?`,
        buttonLabel: "Pause",
        onButtonClick: () =>
          handlePauseUnpause(configurationIds, true, configurationId === null),
        buttonLabel2: "Cancel",
      }),
    );
  };

  const validateUnpause = (configurationId = null) => {
    let configurationIds = [];
    if (configurationId !== null) {
      //single row, from 3 dot menu
      configurationIds = [configurationId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Configurations Selected",
            message: "Please select a configuration to unpause.",
          }),
        );
        return;
      }
      configurationIds = selectedRows
        .filter((y) => y.IsPaused === true)
        .map((x) => x.NotificationConfigurationId);
    }
    if (configurationIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          title: "No Configurations to Unpause",
          message: "Please select a paused configuration to unpause.",
        }),
      );
      return;
    }
    const configurationText = getConfigurationText(configurationIds.length);
    const configurationIsPlural = configurationIds.length !== 1;
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: `${configurationIsPlural ? "These" : "This"} ${configurationText} ${configurationIsPlural ? "are" : "is"} currently paused`,
        message: `Are you sure you want to unpause ${configurationIsPlural ? "these" : "this"} ${configurationText}?`,
        buttonLabel: "Unpause",
        onButtonClick: () =>
          handlePauseUnpause(configurationIds, false, configurationId === null),
        buttonLabel2: "Cancel",
      }),
    );
  };

  const handlePauseUnpause = (
    configurationIds = [],
    setToPaused,
    heapIOIsBulkAction,
  ) => {
    axios
      .patch("/notifications/pause-configurations", null, {
        params: {
          configurationIdsJson: JSON.stringify(configurationIds),
          setToPaused,
        },
      })
      .then((response) => {
        const unableToUpdate = response.data;
        const unableToUpdateConfigurationIds = Object.keys(unableToUpdate);
        if (unableToUpdateConfigurationIds.length === 0) {
          const configurationText = getConfigurationText(configurationIds.length);
          const configurationIsPlural = configurationIds.length !== 1;
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              title: "Successfully saved changes",
              message: `The ${configurationText} ${configurationIsPlural ? "have" : "has"} been updated.`,
            }),
          );

          // heap.io
          if (heapIOIsBulkAction) {
            if (setToPaused) {
              window.heap.track("Configurations - Paused");
            } else {
              window.heap.track("Configurations - Unpaused");
            }
          }
        } else {
          const configurationText = getConfigurationText(
            unableToUpdateConfigurationIds.length,
          );
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Unable to save changes",
              message:
                `The following ${configurationText} could not be updated: ` +
                unableToUpdateConfigurationIds.map((x) => unableToUpdate[x]).join(", "),
            }),
          );
        }
      })
      .then(() => {
        getNotificationConfigurations();
      });
  };

  const deleteConfiguration = (configurationIds = null, heapIOIsBulkAction) => {
    if (configurationIds === -1 || configurationIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Configuration Delete",
          message: "Please select a valid configuration to delete!",
          buttonLabel: "Close",
          onClose: () => getNotificationConfigurations(),
        }),
      );
      console.error(
        "Please select a valid configuration to delete!",
        configurationIds,
      );
      return;
    }

    axios
      .delete("/notifications/delete-configurations", {
        params: { configurationIdsJson: JSON.stringify(configurationIds) },
      })
      .then((response) => {
        const unableToUpdate = response.data;
        const unableToUpdateConfigurationIds = Object.keys(unableToUpdate);
        if (unableToUpdateConfigurationIds.length === 0) {
          const configurationText = getConfigurationText(configurationIds.length);
          const configurationIsPlural = configurationIds.length !== 1;
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Configuration Delete",
              message: `The ${configurationText} ${configurationIsPlural ? "were" : "was"} deleted successfully!`,
              buttonLabel: "Close",
              onClose: () => getNotificationConfigurations(), // trigger grid refresh with updated data
            }),
          );

          // heap.io
          if (heapIOIsBulkAction) {
            window.heap.track("Notification Configurations - Delete");
          }
        } else {
          const configurationText = getConfigurationText(
            unableToUpdateConfigurationIds.length,
          );
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: `Unable to delete ${configurationText}`,
              message:
                `The following ${configurationText} could not be deleted: ` +
                unableToUpdateConfigurationIds.map((x) => unableToUpdate[x]).join(", "),
              buttonLabel: "Close",
              onClose: () => getNotificationConfigurations(),
            }),
          );
        }
      })
      .catch((error) => console.error(error));
  };

  const validateDelete = (configurationId = null) => {
    let configurationIds = [];
    if (configurationId !== null) {
      //single row, from 3 dot menu
      configurationIds = [configurationId];
    } else {
      //batch operation
      const map = gridRef?.current.getSelectedRows();
      const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
      if (selectedRows.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "single",
            title: "No Configurations Selected.",
            message: "Please select a configuration to delete.",
          }),
        );
        return;
      }
      configurationIds = selectedRows.map((x) => x.NotificationConfigurationId);
    }
    const configurationText = getConfigurationText(configurationIds.length);
    const configurationIsPlural = configurationIds.length !== 1;
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: `${configurationIsPlural ? "These" : "This"} ${configurationText} will be deleted`,
        message: `Are you sure you want to delete ${configurationIsPlural ? "these" : "this"} ${configurationText}?`,
        buttonLabel: "Delete",
        onButtonClick: () =>
          deleteConfiguration(configurationIds, configurationId === null),
        buttonLabel2: "Close",
      }),
    );
  };

  const getRowHeight = (params) => {
    const items = Math.max(
      params.model.Severities?.length,
      params.model.OperationIds?.length,
    );
    return Math.max(60, items * 28);
  };

  return (
    <Container>
      <MainNavigation selected={MenuItemEnum.Notifications} />

      <Box
        className="content-container"
        style={{
          boxSizing: "border-box",
        }}
      >
        <Box style={{ display: "flex", justifyContent: "flex-end" }}>
          <ShepHorizontalMenu actions={actions} />
        </Box>
        <AddConfigurationPopup
          open={addConfigurationOpen}
          onClose={onAddConfigurationClosed}
          selectedConfiguration={selectedConfiguration}
          configurations={rows}
        />
        <Container
          sx={{
            height: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 40px",
            borderRadius: "4px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            marginTop: "24px",
          }}
        >
          <Typography variant="h4">Notification Configurations</Typography>
        </Container>

        <ShepDataGridPro
          apiRef={gridRef}
          isDataLoaded={configurationGridLoaded}
          secondsToStall={20}
          rows={rows}
          columns={columns}
          columnHeaderHeight={78}
          getRowId={(row) => row.NotificationConfigurationId}
          getRowHeight={getRowHeight}
          pagination
          initialState={{
            pagination: {
              paginationModel: { pageSize: 15, page: 0 },
            },
          }}
          whiteHeader={true}
          checkboxSelection={true}
          disableMultipleRowSelection={true}
          disableColumnMenu={true}
          disableColumnFilter={true}
          disableColumnReorder={true}
          hideFooterSelectedRowCount={true}
          getRowClassName={(params) => {
            return params.row.IsPaused ? "row-paused" : "";
          }}
          slots={{
            baseCheckbox: ShepCheckbox,
            pagination: ShepPagination,
          }}
          sx={{
            color: "var(--text-primary)",
            fontSize: "var(--font-sm)",
            fontFamily: "var(--font-family)",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            backgroundColor: "var(--default-light)",
            border: "none",
            "& .MuiDataGrid:hover": {
              backgroundColor: "inherit", // Or 'transparent' or whatever color you'd like
            },
            "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
              opacity: 0,
              borderBottom: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              alignItems: "flex-start",
              paddingTop: "4px",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "var(--tertiary-13)",
            },
            "& .Mui-selected": {
              backgroundColor: "var(--primary-pale)",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none",
              },
            "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
              {
                opacity: 1,
              },
            "& .Mui-selected .MuiDataGrid-cellCheckbox": {
              opacity: 1,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "var(--font-md)",
              fontWeight: "var(--font-bold)",
              borderLeft: "none",
            },
            "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
              borderBottomLeftRadius: "4px",
              borderBottomRightRadius: "4px",
              backgroundColor: "var(--primary-pale)",
              borderBottom: "none",
              borderLeft: "none",
            },
            "& .row-paused": {
              color: "text.secondary",
            },
          }}
        />
      </Box>
    </Container>
  );
};

export default NotificationConfig;
